import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatIDR, formatDate, STATUS_BADGE } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReceiptText, CheckCircle2, Clock, AlertCircle } from "lucide-react";

export default function SupplierPortal() {
  const [bills, setBills] = useState([]);
  useEffect(() => { api.get("/bills").then((r) => setBills(r.data)); }, []);

  const totalUnpaid = bills.filter((b) => b.status !== "Lunas").reduce((s, b) => s + b.amount, 0);
  const totalPaid = bills.filter((b) => b.status === "Lunas").reduce((s, b) => s + b.amount, 0);
  const pending = bills.filter((b) => b.status === "Menunggu Verifikasi").length;

  const Stat = ({ label, value, icon: Icon, accent, testid }) => (
    <Card data-testid={testid} className="p-3 sm:p-5">
      <div className="flex items-center gap-2.5 sm:gap-3">
        <div className={`grid place-items-center h-9 w-9 sm:h-11 sm:w-11 rounded-xl shrink-0 ${accent.bg}`}><Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${accent.text}`} /></div>
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="font-heading text-base sm:text-xl font-extrabold mt-0.5 truncate">{value}</p>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Portal Supplier</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Daftar tagihan dan status pembayaran Anda.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat testid="supplier-unpaid" label="Belum Dibayar" value={formatIDR(totalUnpaid)} icon={AlertCircle}
          accent={{ bg: "bg-rose-100 dark:bg-rose-950", text: "text-rose-600" }} />
        <Stat testid="supplier-paid" label="Sudah Dibayar" value={formatIDR(totalPaid)} icon={CheckCircle2}
          accent={{ bg: "bg-emerald-100 dark:bg-emerald-950", text: "text-emerald-600" }} />
        <Stat testid="supplier-pending" label="Menunggu Verifikasi" value={`${pending} invoice`} icon={Clock}
          accent={{ bg: "bg-amber-100 dark:bg-amber-950", text: "text-amber-600" }} />
      </div>

      <Card className="overflow-hidden">
        <div className="px-4 pt-4 flex items-center gap-2">
          <ReceiptText className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-semibold">Daftar Tagihan Saya</h3>
        </div>
        <div className="hidden lg:block mt-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. Invoice</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead>Tgl Terbit</TableHead>
                <TableHead>Jatuh Tempo</TableHead>
                <TableHead>Dibayar</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Belum ada tagihan.</TableCell></TableRow>}
              {bills.map((b) => (
                <TableRow key={b._id} data-testid={`portal-bill-${b.invoice_no}`}>
                  <TableCell className="font-medium">{b.invoice_no}</TableCell>
                  <TableCell className="text-muted-foreground whitespace-normal break-words max-w-[220px] align-top">
                    {b.description}
                    {(b.ucapan || b.dari || b.alamat) && (
                      <div className="mt-1 space-y-0.5 text-[11px] whitespace-pre-line break-words" data-testid={`portal-bill-ucapan-${b.invoice_no}`}>
                        {b.ucapan && <p>Ucapan: {b.ucapan}</p>}
                        {b.dari && <p>Dari: {b.dari}</p>}
                        {b.alamat && <p>Alamat: {b.alamat}</p>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(b.issue_date)}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(b.due_date)}</TableCell>
                  <TableCell className="whitespace-nowrap">{b.paid_date ? formatDate(b.paid_date) : "-"}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatIDR(b.amount)}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_BADGE[b.status]}>{b.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="lg:hidden divide-y divide-border mt-2">
          {bills.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">Belum ada tagihan.</p>}
          {bills.map((b) => (
            <div key={b._id} data-testid={`portal-bill-${b.invoice_no}`} className="p-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{b.invoice_no}</p>
                  <p className="text-[11px] text-muted-foreground">Terbit {formatDate(b.issue_date)} · Tempo {formatDate(b.due_date)}</p>
                </div>
                <Badge variant="outline" className={`${STATUS_BADGE[b.status]} text-[10px] px-1.5 py-0 shrink-0`}>{b.status}</Badge>
              </div>
              {b.description && <p className="text-[11px] text-muted-foreground break-words whitespace-pre-line">{b.description}</p>}
              {(b.ucapan || b.dari || b.alamat) && (
                <div className="space-y-0.5 text-[11px] text-muted-foreground whitespace-pre-line break-words" data-testid={`portal-bill-ucapan-${b.invoice_no}`}>
                  {b.ucapan && <p>Ucapan: {b.ucapan}</p>}
                  {b.dari && <p>Dari: {b.dari}</p>}
                  {b.alamat && <p>Alamat: {b.alamat}</p>}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-muted-foreground">Dibayar: {b.paid_date ? formatDate(b.paid_date) : "-"}</span>
                <span className="text-sm font-bold whitespace-nowrap">{formatIDR(b.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
